import axios from "axios";

// import authApi from "../../utils/axios";
import { authApi, noAuthApi } from "../../utils/axios";
import { config } from "../../utils/config";
import { fixedValues } from "../../utils/fixedValues";

export const createTournament = async (tournamentData, onSuccess, onFail) => {
  try {
    const response = await authApi({
      method: "POST",
      url: "/tournaments",
      data: tournamentData,
    });

    if (onSuccess) {
      onSuccess(response.data);
    }
    return response.data;
  } catch (error) {
    if (onFail) {
      onFail(error);
    }
    throw error;
  }
};

export const getTournaments = async (
  organisationId,
  page = 1,
  limit = 10,
  search = "",
  onSuccess,
  onFail
) => {
  try {
    const response = await authApi({
      method: "GET",
      url: "/tournaments",
      params: { organisationId, page, limit, search },
    });

    if (onSuccess) {
      onSuccess(response.data);
    }
    return response.data;
  } catch (error) {
    if (onFail) {
      onFail(error);
    }
    throw error;
  }
};

export const getTournamentById = async (id, onSuccess, onFail) => {
  try {
    // Use regular axios for public routes
    const response = await axios({
      method: "GET",
      url: `${process.env.REACT_APP_API_BASE_URL}/${config.API_PATH}/tournaments/${id}`,
    });

    if (onSuccess) {
      onSuccess(response.data);
    }
    return response.data;
  } catch (error) {
    if (onFail) {
      onFail(error);
    }
    throw error;
  }
};

export const updateTournament = async (id, updateData, onSuccess, onFail) => {
  try {
    const response = await authApi({
      method: "PATCH",
      url: `/tournaments/${id}`,
      data: updateData,
    });

    if (onSuccess) {
      onSuccess(response.data);
    }
    return response.data;
  } catch (error) {
    if (onFail) {
      onFail(error);
    }
    throw error;
  }
};

export const updateDefaultSelection = async (updateData, onSuccess, onFail) => {
  try {
    const response = await authApi({
      method: "POST",
      url: `/tournaments/selected`,
      data: updateData,
    });

    if (onSuccess) {
      onSuccess(response.data);
    }
    return response.data;
  } catch (error) {
    if (onFail) {
      onFail(error);
    }
    throw error;
  }
};

export const deleteTournament = async (id, onSuccess, onFail) => {
  try {
    const response = await authApi({
      method: "DELETE",
      url: `/tournaments/${id}`,
    });

    // Consider both 200 and 204 as success
    if (response.status === 200 || response.status === 204) {
      if (onSuccess) {
        onSuccess(response.data);
      }
      return true; // Return true to indicate success
    }
    throw new Error("Failed to delete tournament");
  } catch (error) {
    // If the tournament is already deleted (404), consider it a success
    if (error.response?.status === 404) {
      if (onSuccess) {
        onSuccess();
      }
      return true;
    }

    if (onFail) {
      onFail(error);
    }
    throw error;
  }
};

export const getSelectedTournament = async () => {
  try {
    const response = await authApi({
      method: "GET",
      url: "/tournaments/selected",
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateSelectedTournament = async (
  id,
  updateData,
  onSuccess,
  onFail
) => {

  try {
    const response = await authApi({
      method: "PATCH",
      url: `/tournaments/${id}/select`,
      data: updateData,
    });

    if (onSuccess) {
      onSuccess(response.data);
    }
    return response.data;
  } catch (error) {
    if (onFail) {
      onFail(error);
    }
    throw error;
  }
};

export const getSelectedTournamentFromApi = async (organisationId) => {
  try {
    const response = await authApi({
      method: "GET",
      url: "/tournaments/selected",
      params: { organisationId },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getTournamentDashboardFromApi = async (tournamentId) => {
  const response = await authApi({
    method: "GET",
    url: `/tournaments/${tournamentId}/dashboard`,
  });
  return response.data;
};

export const uploadTournamentLogo = async (tournamentId, file) => {
  try {
    const formData = new FormData();
    formData.append("files", file.get("logo"));

    const uploadResponse = await authApi({
      method: "POST",
      url: `/files/upload`,
      params: {
        tournamentId,
        purpose: fixedValues.IMAGE_PURPOSE.TOURNAMENT_IMG,
      },
      headers: {
        "Content-Type": "multipart/form-data",
      },
      data: formData,
    });

    const s3Url = uploadResponse.data[0].s3Url;

    const response = await authApi({
      method: "PATCH",
      url: `/tournaments/${tournamentId}`,
      data: { logo: s3Url },
    });

    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to upload logo";
  }
};

export const updateTournamentSport = async (tournamentId, sport) => {
  try {
    const response = await authApi({
      method: "PATCH",
      url: `/tournaments/${tournamentId}`,
      data: { sport },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to update sport";
  }
};

export const getTournamentResults = async (
  tournamentId,
  categoryId,
  onSuccess,
  onFail
) => {
  try {
    const response = await authApi({
      method: "POST",
      url: `/tournaments/${tournamentId}/category-results`,
      data: { categoryId },
    });

    if (onSuccess) {
      onSuccess(response.data);
    }
    return response.data;
  } catch (error) {
    if (onFail) {
      onFail(error);
    }
    throw error;
  }
};

export const getStandings = async (
  tournamentId,
  categoryId,
  onSuccess,
  onFail
) => {
  try {
    const response = await authApi({
      method: "POST",
      url: `/tournaments/${tournamentId}/category-standings`,
      data: { categoryId },
    });

    if (onSuccess) {
      onSuccess(response.data);
    }
    return response.data;
  } catch (error) {
    if (onFail) {
      onFail(error);
    }
    throw error;
  }
};

export const createFixture = async (
  tournamentId,
  fixtureData,
  onSuccess,
  onFail
) => {
  try {
    const response = await authApi({
      method: "POST",
      url: `/tournaments/${tournamentId}/create-fixture`,
      data: fixtureData,
    });

    if (onSuccess) {
      onSuccess(response.data);
    }
    return response.data;
  } catch (error) {
    if (onFail) {
      onFail(error);
    }
    throw error;
  }
};

export const createCustomFixture = async (tournamentId, fixtureData) => {
  try {
    const response = await authApi({
      method: "POST",
      url: `/tournaments/${tournamentId}/create-custom-fixture`,
      data: fixtureData,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const autoGenerateFixtures = async (payload, onSuccess, onFail) => {
  try {
    const tournamentId = payload.tournamentId;
    const categoryId = payload.categoryId || payload.category?._id;
    const numberOfMatches =
      payload.numberOfMatches ??
      payload.body?.numberOfMatches ??
      payload.body?.faceEachOther ??
      1;

    if (!tournamentId || !categoryId) {
      throw new Error('Missing required parameters for generating fixtures');
    }

    const response = await authApi({
      method: "POST",
      url: `/tournaments/${tournamentId}/autogenerate-fixtures`,
      data: {
        categoryId,
        numberOfMatches
      },
    });

    if (onSuccess) {
      onSuccess(response.data);
    }
    return response.data;
  } catch (error) {
    if (onFail) {
      onFail(error);
    }
    throw error;
  }
};

export const generateAllFixtures = async (payload, onSuccess, onFail) => {
  try {
    const response = await authApi({
      method: "POST",
      url: `/tournaments/${payload.tournamentId}/categories/${payload.categoryId}/generate-all-fixtures`,
      data: { phaseSettings: payload.phaseSettings, timezone: payload.timezone },
    });

    if (onSuccess) {
      onSuccess(response.data);
    }
    return response.data;
  } catch (error) {
    if (onFail) {
      onFail(error);
    }
    throw error;
  }
};

export const getTeamStats = async (
  tournamentId,
  categoryId,
  teamId
) => {
  try {
    const response = await authApi({
      method: "GET",
      url: `/tournaments/${tournamentId}/categories/${categoryId}/teams/${teamId}/stats`,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCategoryStats = async (
  tournamentId,
  categoryId,
  onSuccess,
  onFail
) => {
  try {
    const response = await authApi({
      method: "GET",
      url: `/tournaments/${tournamentId}/categories/${categoryId}/stats`,
    });

    if (onSuccess) {
      onSuccess(response.data);
    }
    return response.data;
  } catch (error) {
    if (onFail) {
      onFail(error);
    }
    throw error;
  }
};


export const createPhase = async (
  tournamentId,
  categoryId,
  phaseData
) => {
  try {
    const response = await authApi({
      method: "POST",
      url: `/tournaments/${tournamentId}/categories/${categoryId}/phases`,
      data: phaseData,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const bulkCreatePhases = async (
  tournamentId,
  categoryId,
  payload
) => {
  try {
    const response = await authApi({
      method: "POST",
      url: `/tournaments/${tournamentId}/categories/${categoryId}/phases/bulk-create`,
      data: payload,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const swapTeamInCategory = async (
  tournamentId,
  categoryId,
  payload
) => {
  try {
    const response = await authApi({
      method: "POST",
      url: `/tournaments/${tournamentId}/categories/${categoryId}/swap-team`,
      data: payload,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getUndoableSwap = async (tournamentId, categoryId) => {
  try {
    const response = await authApi({
      method: "GET",
      url: `/tournaments/${tournamentId}/categories/${categoryId}/swap-team/undoable`,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const undoLastSwap = async (tournamentId, categoryId) => {
  try {
    const response = await authApi({
      method: "POST",
      url: `/tournaments/${tournamentId}/categories/${categoryId}/swap-team/undo`,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createBlock = async (
  tournamentId,
  categoryId,
  phaseId,
  blockData
) => {
  try {
    const response = await authApi({
      method: "POST",
      url: `/tournaments/${tournamentId}/categories/${categoryId}/phases/${phaseId}/blocks`,
      data: blockData,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const assignTeamsToBlock = async (
  tournamentId,
  categoryId,
  phaseId,
  blockId,
  teamsData
) => {
  try {
    const response = await authApi({
      method: "POST",
      url: `/tournaments/${tournamentId}/categories/${categoryId}/phases/${phaseId}/blocks/${blockId}/teams`,
      data: teamsData,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updatePhase = async (
  tournamentId,
  categoryId,
  phaseId,
  phaseData
) => {
  try {
    const response = await authApi({
      method: "PATCH",
      url: `/tournaments/${tournamentId}/categories/${categoryId}/phases/${phaseId}`,
      data: phaseData,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const bulkLockPhases = async (tournamentId, phases) => {
  const response = await authApi({
    method: "PATCH",
    url: `/tournaments/${tournamentId}/phases/lock`,
    data: { phases },
  });
  return response.data;
};

export const deletePhase = async (
  tournamentId,
  categoryId,
  phaseId
) => {
  try {
    const response = await authApi({
      method: "DELETE",
      url: `/tournaments/${tournamentId}/categories/${categoryId}/phases/${phaseId}`,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateBlock = async (
  tournamentId,
  categoryId,
  phaseId,
  blockId,
  blockData
) => {
  try {
    const response = await authApi({
      method: "PATCH",
      url: `/tournaments/${tournamentId}/categories/${categoryId}/phases/${phaseId}/blocks/${blockId}`,
      data: blockData,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteBlock = async (
  tournamentId,
  categoryId,
  phaseId,
  blockId
) => {
  try {
    const response = await authApi({
      method: "DELETE",
      url: `/tournaments/${tournamentId}/categories/${categoryId}/phases/${phaseId}/blocks/${blockId}`,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateTeamsInBlock = async (
  tournamentId,
  categoryId,
  phaseId,
  blockId,
  teamsData
) => {
  try {
    const response = await authApi({
      method: "PATCH",
      url: `/tournaments/${tournamentId}/categories/${categoryId}/phases/${phaseId}/blocks/${blockId}/teams`,
      data: teamsData,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteTeamsFromBlock = async (
  tournamentId,
  categoryId,
  phaseId,
  blockId,
  teamIds
) => {
  try {
    const response = await authApi({
      method: "DELETE",
      url: `/tournaments/${tournamentId}/categories/${categoryId}/phases/${phaseId}/blocks/${blockId}/teams`,
      data: { teamIds },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createProgressionRule = async (
  tournamentId,
  categoryId,
  phaseId,
  ruleData
) => {
  try {
    const response = await authApi({
      method: "POST",
      url: `/tournaments/${tournamentId}/categories/${categoryId}/phases/${phaseId}/rules`,
      data: ruleData,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProgressionRules = async (
  tournamentId,
  categoryId,
  phaseId
) => {
  try {
    const response = await authApi({
      method: "GET",
      url: `/tournaments/${tournamentId}/categories/${categoryId}/phases/${phaseId}/rules`,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateProgressionRule = async (
  tournamentId,
  categoryId,
  phaseId,
  ruleId,
  ruleData
) => {
  try {
    const response = await authApi({
      method: "PATCH",
      url: `/tournaments/${tournamentId}/categories/${categoryId}/phases/${phaseId}/rules/${ruleId}`,
      data: ruleData,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteProgressionRule = async (
  tournamentId,
  categoryId,
  phaseId,
  ruleId
) => {
  try {
    const response = await authApi({
      method: "DELETE",
      url: `/tournaments/${tournamentId}/categories/${categoryId}/phases/${phaseId}/rules/${ruleId}`,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const executeProgressionRules = async (
  tournamentId,
  categoryId,
  phaseId,
  body
) => {
  try {
    const response = await authApi({
      method: "POST",
      url: `/tournaments/${tournamentId}/categories/${categoryId}/phases/${phaseId}/rules/execute`,
      data: body,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const goLive = async (payload, onSuccess, onFail) => {
  try {
    const response = await authApi({
      method: "POST",
      url: `/tournaments/${payload.tournamentId}/categories/${payload.categoryId}/go-live`,
    });

    if (onSuccess) {
      onSuccess(response.data);
    }
    return response.data;
  } catch (error) {
    if (onFail) {
      onFail(error);
    }
    throw error;
  }
};

export const updateFixture = async (
  tournamentId,
  categoryId,
  phaseId,
  blockId,
  fixtureId,
  fixtureData,
  onSuccess,
  onFail
) => {
  try {
    const response = await authApi({
      method: "PATCH",
      url: `/tournaments/${tournamentId}/categories/${categoryId}/phases/${phaseId}/blocks/${blockId}/fixtures/${fixtureId}`,
      data: fixtureData,
    });

    if (onSuccess) {
      onSuccess(response.data);
    }
    return response.data;
  } catch (error) {
    if (onFail) {
      onFail(error);
    }
    throw error;
  }
};

export const getTournamentStandings = async (
  tournamentId,
  categoryId,
  onSuccess,
  onFail
) => {
  try {
    const response = await authApi({
      method: "POST",
      url: `/tournaments/${tournamentId}/category-standings`,
      data: { categoryId },
    });

    if (onSuccess) {
      onSuccess(response.data);
    }
    return response.data;
  } catch (error) {
    if (onFail) {
      onFail(error);
    }
    throw error;
  }
};

export const getTournamentStatistics = async (
  tournamentId,
  categoryId,
  onSuccess,
  onFail
) => {
  try {
    const response = await authApi({
      method: "GET",
      url: `/tournaments/${tournamentId}/categories/${categoryId}/stats`,
      params: { categoryId },
    });

    if (onSuccess) {
      onSuccess(response.data);
    }
    return response.data;
  } catch (error) {
    if (onFail) {
      onFail(error);
    }
    throw error;
  }
};


export async function fetchTournamentPublicStats(tournamentId, onSuccess, onFail) {
  try {
    const resp = await noAuthApi({
      method: "GET",
      url: `/tournaments/${tournamentId}/seasons-data/public-stats`,
    });

    if (typeof onSuccess === "function") onSuccess(resp?.data);
  } catch (err) {
    if (typeof onFail === "function") onFail(err);
  }
}

export const updateTeamExtraPoints = async (
  tournamentId,
  categoryId,
  phaseId,
  blockId,
  teamId,
  extraPoints
) => {
  try {
    const response = await authApi.patch(
      `/tournaments/${tournamentId}/categories/${categoryId}/phases/${phaseId}/blocks/${blockId}/teams/${teamId}/extra-points`,
      { extraPoints }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const respondToMemberInvite = async (tournamentId, response) => {
  const res = await authApi({
    method: "POST",
    url: `/tournaments/${tournamentId}/members/respond`,
    data: { response },
  });
  return res.data;
};

export const addFixtureResult = async (tournamentId, resultData) => {
  try {

    const response = await authApi({
      method: "PATCH",
      url: `/tournaments/${tournamentId}/category-fixture/add-result`,
      data: { resultData },
    });


    return response.data;
  } catch (error) {
    throw error;
  }
};
